import { useState } from "react";
import { Grid, FormControlLabel, Switch } from "@material-ui/core";
import { useTranslation } from 'react-i18next';
import { GeneralModal } from ".";
import {
  ButtonWithLoadingIndicator,
  CancelButton,
  TextFieldInput,
} from "../components-atoms";
import { ITheme } from "../constants/theme";
import { makeStyles } from "@material-ui/styles";

interface IProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (data: InviteMemberData) => void;
  loading?: boolean;
}

export interface InviteMemberData {
  FirstName: string;
  LastName: string;
  Email: string;
  ApprovalRequired: boolean;
  ApprovalThreshold: number;
}

export const InviteMemberModal = ({
  visible,
  onClose,
  onSubmit,
  loading = false
}: IProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [formData, setFormData] = useState<InviteMemberData>({
    FirstName: "",
    LastName: "",
    Email: "",
    ApprovalRequired: false,
    ApprovalThreshold: 0,
  });

  const handleInputChange = (field: keyof InviteMemberData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.type === 'checkbox' 
      ? event.target.checked 
      : event.target.type === 'number'
      ? parseFloat(event.target.value) || 0
      : event.target.value;
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const onFormSubmit = () => {
    onSubmit(formData);
  };

  const handleClose = () => {
    // Reset form data
    setFormData({
      FirstName: "",
      LastName: "",
      Email: "",
      ApprovalRequired: true,
      ApprovalThreshold: 0,
    });
    onClose();
  };

  const isFormValid = () => {
    return formData.FirstName.trim() && 
           formData.LastName.trim() && 
           formData.Email.trim() &&
           (!formData.ApprovalRequired || formData.ApprovalThreshold > 0);
  };

  return (
    <GeneralModal
      title="Invite New Member"
      visible={visible}
      onClose={handleClose}
    >
      <form>
        <GeneralModal.ContentArea>
          <fieldset className={classes.fieldset}>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <TextFieldInput
                  label="First Name"
                  value={formData.FirstName}
                  onChange={handleInputChange('FirstName')}
                  placeholder="Enter first name"
                  required
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextFieldInput
                  label="Last Name"
                  value={formData.LastName}
                  onChange={handleInputChange('LastName')}
                  placeholder="Enter last name"
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextFieldInput
                  label="Email Address"
                  type="email"
                  value={formData.Email}
                  onChange={handleInputChange('Email')}
                  placeholder="Enter email address"
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.ApprovalRequired}
                      onChange={handleInputChange('ApprovalRequired')}
                      color="primary"
                    />
                  }
                  label="Requires Approval for Orders"
                />
              </Grid>
              
              {formData.ApprovalRequired && (
                <Grid item xs={12}>
                  <TextFieldInput
label={`Approval Threshold (${t('currencySymbol')})`}
                    type="number"
                    value={formData.ApprovalThreshold}
                    onChange={handleInputChange('ApprovalThreshold')}
                    placeholder="Enter approval threshold"
                    required={formData.ApprovalRequired}
                    InputProps={{
startAdornment: <span className={classes.prefix}>{t('currencySymbol')}</span>,
                    }}
                  />
                </Grid>
              )}
            </Grid>
          </fieldset>
        </GeneralModal.ContentArea>
        
        <GeneralModal.ButtonArea>
          <CancelButton onClick={handleClose} />
          <ButtonWithLoadingIndicator
            onClick={onFormSubmit}
            disabled={loading || !isFormValid()}
            title="Send Invitation"
            type="submit"
            loading={loading}
          />
        </GeneralModal.ButtonArea>
      </form>
    </GeneralModal>
  );
};

const useStyles = makeStyles((theme: ITheme) => ({
  fieldset: {
    margin: "16px 0 0",
    padding: 0,
    border: 0,
  },
  prefix: {
    color: theme.palette.action.disabled,
    marginRight: 5,
    marginTop: 2,
  },
}));
